import { useEffect, useMemo, useRef, useState } from 'react';
import type { AresCompanyResult } from '@paladin-monorepo/common';
import API from '@/lib/api';
import { debounce } from '@/utils/debounce';

export interface UseAresCompanySearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  onSearchError?: (error: unknown) => void;
  onSearchSuccess?: (results: AresCompanyResult[]) => void;
}

export function useAresCompanySearch({
  debounceMs = 400,
  minQueryLength = 3,
  onSearchError,
  onSearchSuccess,
}: UseAresCompanySearchOptions = {}) {
  const [results, setResults] = useState<AresCompanyResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const skipNextSearchRef = useRef(false);

  const search = useMemo(
    () =>
      debounce((query: string) => {
        if (skipNextSearchRef.current) {
          skipNextSearchRef.current = false;
          return;
        }
        if (!query || query.length < minQueryLength) {
          setResults([]);
          return;
        }

        setIsSearching(true);
        const params = new URLSearchParams();
        if (query.length === 8 && /^[0-9]{8}$/.test(query)) {
          params.append('companyId', query);
        } else {
          params.append('companyName', query);
        }

        API.get<AresCompanyResult[]>(`/ares?${params.toString()}`)
          .then((response) => {
            setResults(response.data);
            onSearchSuccess?.(response.data);
          })
          .catch((error) => {
            setResults([]);
            onSearchError?.(error);
          })
          .finally(() => {
            setIsSearching(false);
          });
      }, debounceMs),
    [debounceMs, minQueryLength, onSearchError, onSearchSuccess],
  );

  useEffect(() => () => search.cancel(), [search]);

  return {
    results,
    setResults,
    isSearching,
    search,
    skipNextSearchRef,
  };
}
